import type { Transaction } from '../types';

export interface TransactionBin {
  label: string;
  min: number;
  max: number;
  count: number;
  totalValue: number;
  color: string;
  transactions: Transaction[];
}

export type BinMetric = 'count' | 'value';

const DEFAULT_BIN_RANGES: { min: number; max: number }[] = [
  { min: 0, max: 50 },
  { min: 50, max: 100 },
  { min: 100, max: 250 },
  { min: 250, max: 500 },
  { min: 500, max: 1000 },
  { min: 1000, max: 2500 },
  { min: 2500, max: 5000 },
  { min: 5000, max: Infinity },
];

const BIN_COLORS = [
  '#22c55e',
  '#65a30d',
  '#ca8a04',
  '#ea580c',
  '#dc2626',
  '#b91c1c',
  '#991b1b',
  '#7f1d1d',
];

function formatBinLabel(min: number, max: number): string {
  const formatNumber = (n: number) =>
    n >= 1000 ? `$${(n / 1000).toFixed(0)}k` : `$${n}`;

  if (max === Infinity) {
    return `${formatNumber(min)}+`;
  }
  return `${formatNumber(min)}-${formatNumber(max)}`;
}

export function calculateTransactionBins(
  transactions: Transaction[]
): TransactionBin[] {
  const bins: TransactionBin[] = DEFAULT_BIN_RANGES.map((range, index) => ({
    label: formatBinLabel(range.min, range.max),
    min: range.min,
    max: range.max,
    count: 0,
    totalValue: 0,
    color: BIN_COLORS[index] ?? BIN_COLORS[BIN_COLORS.length - 1],
    transactions: [],
  }));

  transactions.forEach((transaction) => {
    if (transaction.type !== 'expense') return;
    if (transaction.excludedFromBudget) return;

    const amount = Math.abs(transaction.amount);

    const bin = bins.find((b) => amount >= b.min && amount < b.max);
    if (bin) {
      bin.count += 1;
      bin.totalValue += amount;
      bin.transactions.push(transaction);
    }
  });

  return bins;
}
